
import React, { useEffect, useState } from 'react';
import { format, parseISO, isBefore, parse, addDays, subDays } from 'date-fns';
import { CheckIcon, PlusIcon, XCircleIcon, ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

export default function WorkCoach() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({ Title: '', Content: '', CalendarDate: '', TimeFrom: '', TimeTo: '' });

  const yyyy = (d) => format(d, 'yyyy-MM-dd');

  const fetchTodays = async (date = selectedDate) => {
    setLoading(true);
    try {
      const formatted = yyyy(date);
      const res = await fetch(`https://api.insightos.co.uk/api/todos/${formatted}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await res.json();
      console.log('Fetched todos:', data);
      setTodos(data.data);
    } catch (err) {
      console.error(err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodays(selectedDate);
  }, [selectedDate]);

  const goToPreviousDay = () => {
    setSelectedDate(prev => subDays(prev, 1));
  };

  const goToNextDay = () => {
    setSelectedDate(prev => addDays(prev, 1));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAdd = async () => {
    // basic validation
    if (!form.Title || !form.CalendarDate || !form.TimeFrom || !form.TimeTo) {
      alert('Please fill in all required fields: Title, Date, Time From, and Time To');
      return;
    }
    
    if (!confirm('Add this todo item?')) return;
    
    setAdding(true);
    try {
      const res = await fetch('https://api.insightos.co.uk/api/todo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed');
      
      alert('Todo added successfully!');
      setForm({ Title: '', Content: '', CalendarDate: '', TimeFrom: '', TimeTo: '' });
      fetchTodays(selectedDate);
    } catch (err) {
      console.error(err);
      alert('Failed to add todo. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const markLocalDone = (id) => {
    setTodos(prev => prev.map(t => (t.id === id ? { ...t, _localDone: true } : t)));
  };

  const handleApprove = async (id, title) => {
    if (!confirm(`Mark "${title}" as completed?`)) return;
    
    setBusyId(id);
    // optimistic UI
    markLocalDone(id);
    try {
      await fetch('https://api.insightos.co.uk/api/approvetodo', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      
      fetchTodays(selectedDate);
    } catch (err) {
      console.error(err);
      alert('Failed to complete todo. Please try again.');
      // rollback optimistic
      setTodos(prev => prev.map(t => (t.id === id ? { ...t, _localDone: false } : t)));
    } finally {
      setBusyId(null);
    }
  };

  const isCompleted = (t) => {
    // emerald-500 if Complete is 1 (or truthy)
    return Boolean(t._localDone || t.Complete === 1 || t.Complete === '1' || t.Completed === 1 || t.Completed === '1' || t.Approved || t.done || t.approved || t.ApprovedAt || t.CompletedAt || t.completed);
  };

  const isOverdue = (t) => {
    try {
      if (!t.CalendarDate) return false;
      const datePart = format(new Date(t.CalendarDate), 'yyyy-MM-dd');
      const dtString = `${datePart} ${t.TimeTo || '00:00'}`;
      const parsed = parse(dtString, 'yyyy-MM-dd HH:mm', new Date());
      return isBefore(parsed, new Date()) && !isCompleted(t);
    } catch (e) {
      return false;
    }
  };


  return (
    <div
      data-component="workcoach-dashboard"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-3 py-6 sm:px-4 sm:py-8 lg:px-6"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="relative overflow-hidden rounded-3xl border border-white/35 bg-gradient-to-r from-indigo-950 via-sky-700 to-emerald-500 px-5 py-6 shadow-[0_18px_45px_rgba(15,23,42,0.28)] sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute -left-10 top-0 h-36 w-36 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -right-8 bottom-0 h-32 w-32 rounded-full bg-cyan-200/20 blur-3xl" />
          <div className="relative z-10 sm:flex sm:items-center sm:justify-between">
            <div className="sm:flex-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-100/85">Execution Planner</p>
              <h1 className="mt-2 text-2xl font-semibold text-white sm:text-3xl">WorkCoach Dashboard</h1>
              <p className="mt-2 text-xs text-sky-100/90 sm:text-sm">
                Manage your daily Admin Actions efficiently. Completed items appear in green, overdue in red.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/95 shadow-[0_8px_22px_rgba(15,23,42,0.18)] backdrop-blur-xl">
                  {todos.length} total tasks
                </span>
                <span className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/95 shadow-[0_8px_22px_rgba(15,23,42,0.18)] backdrop-blur-xl">
                  {todos.filter((t) => t.Complete === 1).length} completed
                </span>
              </div>
            </div>
            
            {/* Date Navigation */}
            <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2 sm:gap-3 justify-end">
              <div className="flex items-center rounded-2xl border border-white/25 bg-white/15 p-1.5 shadow-[0_12px_30px_rgba(15,23,42,0.2)] backdrop-blur-xl">
                <button
                  data-component="workcoach-date-nav"
                  data-feature="previous-day"
                  onClick={goToPreviousDay}
                  className="rounded-xl p-1.5 text-white transition-colors hover:bg-white/20 sm:p-2"
                  title="Previous day"
                >
                  <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                
                <div className="min-w-[110px] px-2 py-1 text-center sm:min-w-[130px] sm:px-3">
                  <div className="text-[11px] font-semibold text-white sm:text-xs md:text-sm">
                    {format(selectedDate, 'EEEE')}
                  </div>
                  <div className="text-[10px] text-sky-100/90 sm:text-[11px] md:text-xs">
                    {format(selectedDate, 'MMM dd, yyyy')}
                  </div>
                </div>
                
                <button
                  data-component="workcoach-date-nav"
                  data-feature="next-day"
                  onClick={goToNextDay}
                  className="rounded-xl p-1.5 text-white transition-colors hover:bg-white/20 sm:p-2"
                  title="Next day"
                >
                  <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              
              <button
                data-component="workcoach-date-nav"
                data-feature="jump-today"
                onClick={goToToday}
                className="rounded-2xl bg-white px-3 py-2 text-xs font-semibold text-indigo-700 shadow-[0_12px_30px_rgba(15,23,42,0.2)] transition-all hover:bg-slate-50 sm:text-sm"
              >
                Today
              </button>
            </div>
          </div>
        </div>

        {/* Add Todo Form */}
        <div className="rounded-3xl border border-white/35 bg-gradient-to-br from-indigo-100/80 via-white/75 to-sky-100/70 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:p-6">
          <h2 className="mb-3 flex items-center text-base font-semibold text-slate-900 sm:mb-4 sm:text-lg">
            <PlusIcon className="mr-2 h-4 w-4 text-indigo-600 sm:h-5 sm:w-5" />
            Add New Action
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="lg:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Action Title *</label>
              <input
                data-component="workcoach-form-field"
                data-feature="title"
                name="Title"
                placeholder="Enter Admin Action title..."
                value={form.Title}
                onChange={handleChange}
                className="w-full rounded-2xl border border-white/45 bg-white/80 px-2 py-1.5 text-xs shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 sm:px-4 sm:py-3 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                data-component="workcoach-form-field"
                data-feature="calendar-date"
                name="CalendarDate"
                type="date"
                value={form.CalendarDate}
                onChange={handleChange}
                className="w-full rounded-2xl border border-white/45 bg-white/80 px-2 py-1.5 text-xs shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 sm:px-4 sm:py-3 sm:text-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">From *</label>
                <input
                  data-component="workcoach-form-field"
                  data-feature="time-from"
                  name="TimeFrom"
                  type="time"
                  value={form.TimeFrom}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/45 bg-white/80 px-2 py-1.5 text-xs shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 sm:px-3 sm:py-3 sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">To *</label>
                <input
                  data-component="workcoach-form-field"
                  data-feature="time-to"
                  name="TimeTo"
                  type="time"
                  value={form.TimeTo}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/45 bg-white/80 px-2 py-1.5 text-xs shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 sm:px-3 sm:py-3 sm:text-sm"
                />
              </div>
            </div>
            
            <div className="lg:col-span-4">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                data-component="workcoach-form-field"
                data-feature="notes"
                name="Content"
                placeholder="Add any additional notes..."
                value={form.Content}
                onChange={handleChange}
                rows={2}
                className="w-full resize-none rounded-2xl border border-white/45 bg-white/80 px-2 py-1.5 text-xs shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 sm:px-4 sm:py-3 sm:text-sm"
              />
            </div>
            
            <div className="lg:col-span-4 flex flex-wrap justify-end gap-2 sm:gap-3">
              <button
                data-component="workcoach-form-action"
                data-feature="clear-form"
                onClick={() => {
                  setForm({ Title: '', Content: '', CalendarDate: '', TimeFrom: '', TimeTo: '' });
                }}
                className="rounded-2xl border border-white/45 bg-white/80 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] transition-all hover:bg-slate-100 sm:px-4 sm:py-2 sm:text-sm"
              >
                <XCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                Clear
              </button>
              
              <button
                data-component="workcoach-form-action"
                data-feature="submit-form"
                onClick={handleAdd}
                disabled={adding}
                className="rounded-2xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_14px_32px_rgba(49,46,129,0.3)] transition-all hover:from-indigo-800/95 hover:to-indigo-600/95 disabled:cursor-not-allowed disabled:opacity-60 sm:px-4 sm:py-2 sm:text-sm"
              >
                <PlusIcon className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                {adding ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
        {/* Tasks Table */}
        <div className="overflow-hidden rounded-3xl border border-white/35 bg-gradient-to-br from-indigo-100/70 via-white/80 to-sky-100/70 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl">
          <div className="border-b border-white/30 bg-gradient-to-r from-indigo-900/90 via-sky-700/85 to-emerald-500/80 px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="flex items-center text-base font-semibold text-white sm:text-lg">
              <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-indigo-600" />
              My Actions for {format(selectedDate, 'EEEE, MMMM dd')}
            </h2>
          </div>
          
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200/70">
              <thead className="bg-white/60 backdrop-blur-xl">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Task</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Notes</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/70 bg-white/65">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <span className="ml-3 text-gray-600">Loading Admin Actions...</span>
                      </div>
                    </td>
                  </tr>
                ) : todos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">No Admin Actions for this day</p>
                      <p className="text-sm">Add a new Admin Action to get started</p>
                    </td>
                  </tr>
                ) : (
                  <AnimatePresence>
                    {todos.map((t) => {
                      const completed = t.Complete === 1;
                      const incomplete = t.Complete === null || t.Complete === undefined;
                      
                      return (
                        <motion.tr
                          key={t.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                          className={`transition-all duration-200 ${
                            completed 
                              ? 'bg-emerald-500 text-white border-l-4 border-l-emerald-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' 
                              : incomplete 
                              ? 'bg-red-500 text-white border-l-4 border-l-red-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' 
                              : 'hover:bg-slate-50/90'
                          }`}
                        >
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <button
                              data-component="workcoach-complete-action"
                              data-feature={`complete-${t.id}`}
                              onClick={() => handleApprove(t.id, t.Title)}
                              disabled={busyId === t.id || completed}
                              className={`relative inline-flex items-center justify-center w-7 h-7 sm:w-9 sm:h-9 rounded-full transition-all duration-200 ${
                                completed
                                  ? 'bg-emerald-600 text-white cursor-default'
                                  : 'bg-white border-2 border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-600 shadow-[0_8px_20px_rgba(15,23,42,0.12)]'
                              } ${busyId === t.id ? 'animate-pulse' : ''}`}
                            >
                              <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          </td>
                          
                          <td className="px-6 py-4">
                            <div className={`text-sm font-medium ${completed ? 'text-white line-through' : incomplete ? 'text-white' : 'text-gray-900'}`}>
                              {t.Title}
                            </div>
                            <div className={`text-xs mt-1 ${completed ? 'text-emerald-100' : incomplete ? 'text-red-100' : 'text-gray-500'}`}>
                              {t.CalendarDate ? format(new Date(t.CalendarDate), 'MMM dd, yyyy') : 'No date'}
                            </div>
                          </td>
                          
                          <td className="px-6 py-4">
                            <div className={`text-sm ${completed ? 'text-white' : incomplete ? 'text-white' : 'text-gray-600'}`}>
                              {t.Content || '-'}
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${completed ? 'text-white' : incomplete ? 'text-white' : 'text-gray-900'}`}>
                              <div className="flex items-center space-x-1">
                                <span>{t.TimeFrom || '--:--'}</span>
                                <span className={`${completed ? 'text-emerald-200' : incomplete ? 'text-red-200' : 'text-gray-400'}`}>→</span>
                                <span>{t.TimeTo || '--:--'}</span>
                              </div>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden px-4 pb-6">
            {loading ? (
              <div className="py-10 text-center text-gray-600 flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                <span className="mt-3 text-sm">Loading Admin Actions...</span>
              </div>
            ) : todos.length === 0 ? (
              <div className="py-10 text-center text-gray-500">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-base font-medium">No Admin Actions for this day</p>
                <p className="text-xs">Add a new Admin Action to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {todos.map((t) => {
                  const completed = t.Complete === 1;
                  const incomplete = t.Complete === null || t.Complete === undefined;
                  return (
                    <div
                      key={t.id}
                      className={`rounded-2xl border p-4 shadow-[0_14px_30px_rgba(15,23,42,0.16)] backdrop-blur-xl ${
                        completed
                          ? 'bg-emerald-500/90 border-emerald-600 text-white'
                          : incomplete
                          ? 'bg-red-500/90 border-red-600 text-white'
                          : 'bg-white/80 border-white/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className={`text-base font-semibold ${completed || incomplete ? 'text-white' : 'text-gray-900'}`}>{t.Title}</p>
                          <p className={`text-xs mt-1 ${completed ? 'text-emerald-100' : incomplete ? 'text-red-100' : 'text-gray-500'}`}>
                            {t.CalendarDate ? format(new Date(t.CalendarDate), 'MMM dd, yyyy') : 'No date'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleApprove(t.id, t.Title)}
                          disabled={busyId === t.id || completed}
                          className={`flex items-center justify-center w-9 h-9 rounded-full border text-sm font-semibold ${
                            completed
                              ? 'bg-white/20 border-white text-white cursor-default'
                              : 'bg-white border-gray-200 text-emerald-600'
                          } ${busyId === t.id ? 'animate-pulse' : ''}`}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <div className={`mt-4 text-sm ${completed || incomplete ? 'text-white' : 'text-gray-700'}`}>
                        {t.Content || 'No additional notes'}
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <span className={`${completed ? 'text-emerald-100' : incomplete ? 'text-red-100' : 'text-gray-500'}`}>
                          {t.TimeFrom || '--:--'} → {t.TimeTo || '--:--'}
                        </span>
                        {isOverdue(t) && !completed && (
                          <span className="px-2 py-1 rounded-full bg-white/20 text-white text-[10px]">Overdue</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
